import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Chat, AIChat } from 'app/layout/common/quick-chat/quick-chat.types';
import { AIChatService, AIChatConversation, MessageContext } from 'app/core/services/ai-chat.service';
import {
    BehaviorSubject,
    map,
    Observable,
    of,
    switchMap,
    tap,
    throwError,
    catchError,
} from 'rxjs';

@Injectable({ providedIn: 'root' })
export class QuickChatService {
    private _chat: BehaviorSubject<Chat> = new BehaviorSubject(null);
    private _chats: BehaviorSubject<Chat[]> = new BehaviorSubject<Chat[]>(null);
    private _aiChatService = inject(AIChatService);

    /**
     * Constructor
     */
    constructor(private _httpClient: HttpClient) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for chat
     */
    get chat$(): Observable<Chat> {
        return this._chat.asObservable();
    }

    /**
     * Getter for chat
     */
    get chats$(): Observable<Chat[]> {
        return this._chats.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get chats
     */
    getChats(): Observable<any> {
        return this._httpClient.get<Chat[]>('api/apps/chat/chats').pipe(
            tap((response: Chat[]) => {
                this._chats.next(response);
            })
        );
    }

    /**
     * Get chat
     *
     * @param id
     */
    getChatById(id: string): Observable<any> {
        return this._httpClient
            .get<Chat>('api/apps/chat/chat', { params: { id } })
            .pipe(
                map((chat) => {
                    // Update the chat
                    this._chat.next(chat);

                    // Return the chat
                    return chat;
                }),
                switchMap((chat) => {
                    if (!chat) {
                        return throwError(
                            'Could not found chat with id of ' + id + '!'
                        );
                    }

                    return of(chat);
                })
            );
    }

    /**
     * Get AI Chat conversation
     */
    getAIChat(): Observable<Chat> {
        return this._aiChatService.getActiveConversation().pipe(
            map((response) => this.convertAIConversationToChat(response.data)),
            tap((chat) => {
                this._chat.next(chat);
                // Set as single chat for AI Bot
                this._chats.next([chat]);
            }),
            catchError((error) => {
                console.error('Error loading AI conversation:', error);
                // Create empty AI chat if no conversation exists
                const emptyAIChat = this.createEmptyAIChat();
                this._chat.next(emptyAIChat);
                this._chats.next([emptyAIChat]);
                return of(emptyAIChat);
            })
        );
    }

    /**
     * Send message to AI Chat
     */
    sendAIMessage(message: string, context?: MessageContext): Observable<any> {
        // Add user message immediately to the chat
        this.addTemporaryUserMessage(message);
        
        // Add typing indicator
        this.addTypingIndicator();
        
        return this._aiChatService.sendMessage(message, context).pipe(
            tap(() => {
                // Remove typing indicator and refresh conversation
                this.removeTypingIndicator();
            }),
            switchMap(() => this.getAIChat()), // Refresh the conversation after sending
            catchError((error) => {
                // Remove typing indicator on error
                this.removeTypingIndicator();
                throw error;
            })
        );
    }

    /**
     * Add temporary user message to current chat
     */
    private addTemporaryUserMessage(message: string): void {
        const currentChat = this._chat.value;
        if (!currentChat) return;

        const tempMessage = {
            id: 'temp-user-' + Date.now(),
            chatId: currentChat.id,
            contactId: 'current-user',
            isMine: true,
            value: message,
            createdAt: new Date().toISOString()
        };

        const updatedChat = {
            ...currentChat,
            messages: [...(currentChat.messages || []), tempMessage],
            lastMessage: message,
            lastMessageAt: new Date().toISOString()
        };

        this._chat.next(updatedChat);
        this._chats.next([updatedChat]);
    }

    /**
     * Add typing indicator to current chat
     */
    private addTypingIndicator(): void {
        const currentChat = this._chat.value;
        if (!currentChat) return;

        const typingMessage = {
            id: 'typing-indicator',
            chatId: currentChat.id,
            contactId: 'freequest-bot',
            isMine: false,
            value: '<div class="typing-indicator"><span></span><span></span><span></span></div>',
            createdAt: null
        };

        const updatedChat = {
            ...currentChat,
            messages: [...(currentChat.messages || []), typingMessage]
        };

        this._chat.next(updatedChat);
        this._chats.next([updatedChat]);
    }

    /**
     * Remove typing indicator from current chat
     */
    private removeTypingIndicator(): void {
        const currentChat = this._chat.value;
        if (!currentChat) return;

        const updatedMessages = currentChat.messages?.filter(msg => msg.id !== 'typing-indicator') || [];
        
        const updatedChat = {
            ...currentChat,
            messages: updatedMessages
        };

        this._chat.next(updatedChat);
        this._chats.next([updatedChat]);
    }

    /**
     * Convert AI conversation to Chat format
     */
    private convertAIConversationToChat(conversation: AIChatConversation): Chat {
        if (!conversation) {
            return this.createEmptyAIChat();
        }

        return {
            id: conversation._id,
            contactId: 'freequest-bot',
            contact: {
                id: 'freequest-bot',
                name: 'FreeQuest Bot',
                avatar: null,
                about: 'AI Assistant for FreeQuest Academy'
            },
            unreadCount: 0,
            muted: false,
            lastMessage: conversation.messages?.length > 0 
                ? conversation.messages[conversation.messages.length - 1].content 
                : 'Start a conversation...',
            lastMessageAt: conversation.lastMessageAt,
            messages: conversation.messages?.map((msg, index) => ({
                id: msg._id,
                chatId: conversation._id,
                contactId: msg.role === 'user' ? 'current-user' : 'freequest-bot',
                isMine: msg.role === 'user',
                value: msg.content,
                createdAt: msg.createdAt
            })) || []
        };
    }

    /**
     * Create empty AI chat
     */
    private createEmptyAIChat(): Chat {
        return {
            id: 'ai-chat-empty',
            contactId: 'freequest-bot',
            contact: {
                id: 'freequest-bot',
                name: 'FreeQuest Bot',
                avatar: null,
                about: 'AI Assistant for FreeQuest Academy'
            },
            unreadCount: 0,
            muted: false,
            lastMessage: 'Start a conversation...',
            lastMessageAt: new Date().toISOString(),
            messages: []
        };
    }
}
